import { Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import moment from 'moment';
import { ZodError } from 'zod';
import axios from 'axios';
import ContentListComponent from '../../../components/ContentManagement/ContentList';
import { ColumnSpec } from '../../../components/common/DataTable/types';
import UserAvatar from '../../../components/common/UserAvatar/UserAvatar';
import getLocalStorageData, { setLocalStorageData } from '../../../utils/token';
import { ContentRow, User } from '../../../components/ContentManagement/ContentList/types';
import styles from './styles';
import { getApi, putApiWithAuth } from '../../../utils/apiCalls';
import getAPIUrl from '../../../config';
import MyProfileDetails from '../../../components/MyProfile/ProfileDetails';
import EditMyProfile from '../../../components/MyProfile/EditProfile';
import { UserData } from '../../../components/MyProfile/ProfileDetails/types';
import Popup from '../../../components/common/Popup/Popup';
import { SortDirection } from '../../../components/common/DataTable/sortable-header';
import getArticles from './Api';
import editProfileSchema from '../../../utils/schemas';
import { useToast } from '../../../components/common/ToasterContext';
import { showLoaderAfterTimeout, trimFormData } from '../../../utils/helpers';

const ContentManagement = () => {
    const userData = getLocalStorageData();
    const [content, setContent] = useState<ContentRow[]>([]);
    const [rowContentData, setRowContentData] = useState<ContentRow>();
    const [hasMore, setHasMore] = useState(true);
    const [contentCount, setContentCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const size = 10;
    const [openProfileDetails, setOpenProfileDetails] = useState(false);
    const [editProfileDetails, setEditProfileDetails] = useState(false);
    const [userInfo, setUserInfo] = useState<UserData>({
        ...userData,
        password: '',
        repeatPassword: '',
        roleId: '',
    });
    const [formErrors, setFormErrors] = useState<{
        [key: string]: Array<string>;
    }>();
    const [roles, setRoles] = useState<Array<{ name: string; value: number }>>([]);
    const [authors, setAuthors] = useState<Array<{ id: string; name: string; checked: boolean }>>([]);
    const [categories, setCategories] = useState<Array<{ id: string; name: string; checked: boolean }>>([]);
    const user: User = {
        name: `${userData.firstname} ${userData.lastname}`,
        profileImg: userData.signedImageUrl,
    };
    const [selectedRoleId, setSelectedRoleId] = useState<string>('');
    const [selectedImage, setSelectedImage] = useState<string | ArrayBuffer | null | undefined>();
    const [selectedFile, setSelectedFile] = useState<File>();
    const [reload, setReload] = useState(false);
    const [showError, setShowError] = useState<boolean>(false);
    const [searchText, setSearchText] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [applyFilters, setApplyFilters] = useState(false);
    const [loader, setLoader] = useState(true);
    const [changeStatus, setChangeStatus] = useState(false);
    const [suggestedResource, setSuggestedResource] = useState<boolean>(false);
    const [suggestedResourceList, setSuggestedResourceList] = useState<ContentRow[]>([]);
    const [sortKey, setSortkey] = useState<string>('title');
    const [sortOrder, setSortOrder] = useState<string>('asc');
    const [submitting, setSubmitting] = useState<boolean>(false);

    const toast = useToast();

    const columnSpec: ColumnSpec<ContentRow>[] = [
        {
            name: 'title',
            title: 'Header',
            cellProperty: 'title',
            cellRenderer: (row: ContentRow) => (
                <Typography sx={styles.heading} component="div">
                    {suggestedResource ? (
                        <FormControlLabel
                            key={`${row.id}`}
                            id={`${row.title}`}
                            value={row.is_suggested !== 0}
                            name={`${row.title}`}
                            checked={row.is_suggested !== 0}
                            onChange={(_event, checked) => {
                                setContent(
                                    content.map((c) =>
                                        c.id === row.id ? { ...c, is_suggested: checked ? 1 : 0 } : { ...c },
                                    ),
                                );
                                if (
                                    suggestedResourceList.length &&
                                    suggestedResourceList.find((s) => s.id === row.id)
                                ) {
                                    setSuggestedResourceList(
                                        suggestedResourceList.map((it) =>
                                            it.id === row.id ? { ...it, is_suggested: checked ? 1 : 0 } : { ...it },
                                        ),
                                    );
                                } else {
                                    setSuggestedResourceList([
                                        ...suggestedResourceList,
                                        { ...row, is_suggested: checked ? 1 : 0 },
                                    ]);
                                }
                            }}
                            control={<Checkbox key={`radio-${row.id}`} defaultChecked size="small" />}
                            label={<></>}
                        />
                    ) : null}
                    <UserAvatar
                        id="article_icon"
                        name={`${row.title}`}
                        size={40}
                        source={row.hero_image}
                        radius="8px"
                    />
                    <Typography component="span" sx={styles.title}>
                        {row.title}
                    </Typography>
                </Typography>
            ),
        },
        {
            name: 'name',
            title: 'Author',
            cellProperty: 'name',
            cellRenderer: (row: ContentRow) => (
                <Typography component="div" sx={styles.author}>
                    {row.name}
                </Typography>
            ),
        },
        {
            name: 'created_at',
            title: 'Added',
            cellProperty: 'created_at',
            cellRenderer: (row: ContentRow) => <>{moment(new Date(row.created_at)).format('MMM DD, YYYY')}</>,
        },
        {
            name: 'is_visible',
            title: 'Visibility Status',
            cellProperty: 'is_visible',
            cellRenderer: (row: ContentRow) => (
                <Typography
                    component="div"
                    sx={styles.visibility}
                    onClick={() => {
                        setChangeStatus(true);
                        setRowContentData(row);
                    }}
                    id={`${row.name}_article_status`}
                >
                    <img
                        alt="status"
                        src={row.is_visible ? '/images/active-toggle.png' : '/images/disable-toggle.png'}
                    />
                </Typography>
            ),
        },
    ];

    useEffect(() => {
        setLoader(true);
        getArticles({
            size,
            currentPage: 1,
            searchText,
            authors,
            categories,
            selectedStatus,
            sort: sortKey,
            order: sortOrder,
        })
            .then((res) => {
                const contentList = res.data.data;
                setContent([...contentList.articles]);
                setContentCount(contentList.count);
                showLoaderAfterTimeout(setLoader);
            })
            .catch((_error) => {
                showLoaderAfterTimeout(setLoader);
            });
    }, [searchText, applyFilters, reload]);

    const loadMore = () => {
        const loadItems = async () => {
            if (size * (currentPage - 1) < contentCount) {
                const res = await getArticles({
                    size,
                    currentPage: currentPage + 1,
                    searchText,
                    authors,
                    categories,
                    selectedStatus,
                    sort: sortKey,
                    order: sortOrder,
                });
                if (!axios.isAxiosError(res)) {
                    const contentList = res.data.data;
                    const cData = [...content, ...contentList.articles];
                    setCurrentPage(currentPage + 1);
                    setContent(cData);
                    showLoaderAfterTimeout(setLoader);
                }
            }
        };
        setHasMore(!(content.length === contentCount));
        loadItems();
    };

    useEffect(() => {
        getApi(`${getAPIUrl()}/admins/admin-roles`).then((res) => {
            const rolesData = res.data.data.data.map((r: { role: string; id: string }) => ({
                name: r.role,
                value: Number(r.id),
            }));
            setRoles(rolesData);
            const roleData = rolesData.find((role: { name: string; value: number }) => role.name === userData.roleName);
            setSelectedRoleId(String(roleData?.value));
            setUserInfo({ ...userInfo, roleId: String(roleData?.value) });
        });
        getApi(`${getAPIUrl()}/admins/article-authors`).then((res) => {
            setAuthors(res.data.data.map((it: { id: string; name: string }) => ({ ...it, checked: false })));
        });
        getApi(`${getAPIUrl()}/admins/article-categories`).then((res) => {
            setCategories(res.data.data.map((it: { id: string; name: string }) => ({ ...it, checked: false })));
        });
    }, []);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { value, name } = e.target;
        if (name === 'roleId') {
            setSelectedRoleId(value);
        }
        setUserInfo({
            ...userInfo,
            [name]: String(value),
        });
    };

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        try {
            setSubmitting(true);
            editProfileSchema.parse(trimFormData(userInfo));
            const formData = new FormData();
            formData.append('roleId', selectedRoleId);
            formData.append('firstname', userInfo.firstname);
            formData.append('lastname', userInfo.lastname);
            formData.append('imageFile', selectedFile as any);
            if (userInfo.password) {
                formData.append('password', String(userInfo.password || ''));
            }
            if (userInfo.repeatPassword) {
                formData.append('repeatPassword', String(userInfo.repeatPassword || ''));
            }

            const result = await putApiWithAuth(`${getAPIUrl()}/admins/edit-admin`, formData);
            setFormErrors(undefined);
            setUserInfo({
                ...userInfo,
                email: result.data.data.data.email,
                firstname: result.data.data.data.firstname,
                lastname: result.data.data.data.lastname,
                profileImageUrl: result.data.data.data.profileImageUrl,
                signedImageUrl: result.data.data.data.signedImageUrl,
                roleName: roles.find((r) => r.value === Number(result.data.data.data.roleId))?.name || '',
                password: '',
                repeatPassword: '',
            });
            setLocalStorageData({
                ...userData,
                adminRole: {
                    role: roles.find((r) => r.value === Number(result.data.data.data.roleId))?.name || '',
                },
                email: result.data.data.data.email,
                firstname: result.data.data.data.firstname,
                lastname: result.data.data.data.lastname,
                profileImageUrl: result.data.data.data.profileImageUrl,
                signedImageUrl: result.data.data.data.signedImageUrl,
            });
            if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage('Request Processeed');
                toast.onSuccess();
            }
            setSelectedRoleId(result.data.data.data.roleId);
            setEditProfileDetails(false);
            setOpenProfileDetails(false);
        } catch (error: any) {
            if (error instanceof ZodError) {
                setFormErrors(error.flatten().fieldErrors);
            } else if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage(error.response.data.errors);
                toast.onError();
            }
        } finally {
            setSubmitting(false);
        }
    };

    const onStatusChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSelectedStatus(event.target.value);
    };
    const onCategoryChange = (name: string, checked: boolean) => {
        const data = categories.map((c) => (c.name === name ? { ...c, checked } : c));
        setCategories(data);
    };
    const onAuthorChange = (name: string, checked: boolean) => {
        const data = authors.map((c) => (c.name === name ? { ...c, checked } : c));
        setAuthors(data);
    };

    const onFiltersClearClick = () => {
        setSelectedStatus('');
        setCategories(categories.map((c) => ({ ...c, checked: false })));
        setAuthors(authors.map((a) => ({ ...a, checked: false })));
        setApplyFilters(!applyFilters);
        setContent([]);
        setLoader(true);
    };

    const onFilterChipDelete = (type: 'status' | 'category' | 'author', name?: string) => {
        if (type === 'status') {
            setSelectedStatus('');
        } else if (type === 'category' && name) {
            const data = categories.map((c) => (c.name === name ? { ...c, checked: false } : c));
            setCategories(data);
        } else {
            const data = authors.map((a) => (a.name === name ? { ...a, checked: false } : a));
            setAuthors(data);
        }
        setApplyFilters(!applyFilters);
        setContent([]);
        setLoader(true);
    };

    const onFiltersApplyClick = () => {
        setContent([]);
        setCurrentPage(1);
        setApplyFilters(!applyFilters);
        setLoader(true);
    };

    const onSearchChange = (text: string) => {
        setSearchText(text);
        setCurrentPage(1);
    };

    const handleChangeAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
            const reader = new FileReader();
            reader.onload = (event) => {
                setSelectedImage(event?.target?.result);
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const sortData = async (key: string, direction: SortDirection) => {
        const res = await getArticles({
            size,
            currentPage: 1,
            searchText,
            authors,
            categories,
            selectedStatus,
            sort: key,
            order: direction === 0 ? 'desc' : 'asc',
        });
        if (!axios.isAxiosError(res)) {
            const contentList = res.data.data;
            setContent([...contentList.articles]);
            setContentCount(contentList.count);
            setCurrentPage(1);
            setHasMore(true);
            setSortkey(key);
            setSortOrder(direction === 0 ? 'desc' : 'asc');
            showLoaderAfterTimeout(setLoader);
        }
    };

    const onSaveSuggestedResource = async () => {
        try {
            if (suggestedResourceList.length) {
                await putApiWithAuth(`${getAPIUrl()}/admins/update-suggested-resources`, {
                    suggested: suggestedResourceList.filter((it) => it.is_suggested !== 0).map((it) => it.id),
                    notSuggested: suggestedResourceList.filter((it) => it.is_suggested === 0).map((it) => it.id),
                });
                setCurrentPage(1);
                setSuggestedResource(false);
                if (toast) {
                    toast.onToggleSnackbar();
                    toast.onMessage('Request Processeed');
                    toast.onSuccess();
                }
                setReload(!reload);
            }
        } catch (error: any) {
            if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage(error.response.data.errors);
                toast.onError();
            }
        }
    };

    const handleCloseMyProfile = (value: boolean) => {
        setUserInfo({
            ...userData,
            password: '',
            repeatPassword: '',
            roleId: String(
                roles.find((role: { name: string; value: number }) => role.name === userData.roleName)?.value,
            ),
        });
        setEditProfileDetails(value);
        setFormErrors(undefined);
    };

    return (
        <>
            <ContentListComponent
                columns={columnSpec}
                tableData={content}
                user={user}
                loadMore={loadMore}
                hasMore={hasMore}
                openProfileDetails={openProfileDetails}
                setOpenProfileDetails={setOpenProfileDetails}
                categories={categories}
                authors={authors}
                searchChange={onSearchChange}
                selectedStatus={selectedStatus}
                onStatusChange={onStatusChange}
                onCategoryChange={onCategoryChange}
                onAuthorChange={onAuthorChange}
                onFiltersClearClick={onFiltersClearClick}
                onFilterChipDelete={onFilterChipDelete}
                onFiltersApplyClick={onFiltersApplyClick}
                loader={loader}
                sortData={sortData}
                setSuggestedResource={setSuggestedResource}
                suggestedResource={suggestedResource}
                onSaveSuggestedResource={onSaveSuggestedResource}
                setReload={setReload}
                reload={reload}
            />
            <MyProfileDetails
                openProfileDetails={openProfileDetails}
                setOpenProfileDetails={setOpenProfileDetails}
                setEditProfileDetails={setEditProfileDetails}
                userData={userData}
            />
            <EditMyProfile
                editProfileDetails={editProfileDetails}
                userInfo={userInfo}
                handleInput={handleInput}
                handleSubmit={onSubmit}
                roles={roles}
                selectedRoleId={selectedRoleId}
                handleChangeAvatar={handleChangeAvatar}
                selectedImage={selectedImage}
                formErrors={formErrors}
                handleCloseMyProfile={handleCloseMyProfile}
                submitting={submitting}
            />
            <Popup
                id={rowContentData?.is_visible ? 'confirmDeactivation' : 'confirmActivation'}
                open={changeStatus}
                title="Visibility Status"
                textAlign="center"
                handleClose={() => setChangeStatus(false)}
                description={
                    <>
                        {rowContentData?.is_visible
                            ? 'Are you sure you want to hide this content? Doing so will remove its visibility to members on the Diathrive Application.'
                            : 'Are you sure you want to display this content? Displaying the content will make it visible to members on the Diathrive Application.'}
                    </>
                }
            >
                <>
                    <Button
                        id="cancel"
                        variant="outlined"
                        sx={styles.cancel}
                        fullWidth
                        onClick={() => {
                            setChangeStatus(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        id={`continue${rowContentData?.is_visible ? 'Dea' : 'A'}ctivation`}
                        variant="contained"
                        sx={styles.continue}
                        fullWidth
                        onClick={async () => {
                            try {
                                await putApiWithAuth(`${getAPIUrl()}/admins/articles/visibility-status`, {
                                    id: Number(rowContentData?.id),
                                    isVisible: rowContentData?.is_visible === 0,
                                });
                                setCurrentPage(1);
                                setChangeStatus(false);
                                if (toast) {
                                    toast.onToggleSnackbar();
                                    toast.onMessage('Request Processeed');
                                    toast.onSuccess();
                                }
                                setReload(!reload);
                            } catch (error: any) {
                                setShowError(!showError);
                                if (toast) {
                                    toast.onToggleSnackbar();
                                    toast.onMessage(error.response.data.errors);
                                    toast.onError();
                                }
                            }
                        }}
                    >
                        {rowContentData?.is_visible ? 'Hide' : 'Display'}
                    </Button>
                </>
            </Popup>
        </>
    );
};
export default ContentManagement;
